import { roomInviteHomePath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

export const ROOM_INVITE_STORAGE_KEY = "roomInviteBridge:v1";
const TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_DELIVERIES = 32;
const validId = id => typeof id === "string" && id.length > 0 && id.length <= 200;

// Only public invitation context is stored. Authentication remains in the existing account flow.
export function createRoomInviteBridge({ hallOrigin, storage, ready = () => true, onPending = () => {},
  registry = ROOM_INVITE_REGISTRY, now = () => Date.now() }) {
  const ack = (deliveryId, status) => ({ version: 1, deliveryId, status });
  const read = () => {
    try {
      const saved = JSON.parse(storage.getItem(ROOM_INVITE_STORAGE_KEY) || "null");
      const deliveries = saved && saved.version === 1 && Array.isArray(saved.deliveries) ? saved.deliveries.filter(item =>
        validId(item.deliveryId) && typeof item.homePath === "string" && Number.isFinite(item.acceptedAt) &&
        item.acceptedAt <= now() && now() - item.acceptedAt <= TTL).slice(-MAX_DELIVERIES) : [];
      const pending = saved && saved.pending && deliveries.find(item => item.deliveryId === saved.pending.deliveryId &&
        item.homePath === saved.pending.homePath) ? saved.pending : null;
      return { version: 1, deliveries, pending };
    } catch (_) { return { version: 1, deliveries: [], pending: null }; }
  };
  const write = state => { try { storage.setItem(ROOM_INVITE_STORAGE_KEY, JSON.stringify(state)); return true; } catch (_) { return false; } };
  const candidatePath = candidate => {
    if (typeof candidate !== "string" || candidate.length > 4096) return null;
    try {
      const url = new URL(candidate);
      if (url.protocol !== "https:" || url.origin !== hallOrigin || url.pathname !== "/home" || url.username || url.password || url.hash) return null;
      return roomInviteHomePath(url.search, registry);
    } catch (_) { return null; }
  };
  return {
    version: 1,
    receive(envelope) {
      const id = envelope && envelope.deliveryId;
      if (!envelope || envelope.version !== 1 || !validId(id)) return ack(validId(id) ? id : "", "unsupported");
      if (!ready()) return ack(id, "not_ready");
      const homePath = candidatePath(envelope.candidate);
      if (!homePath) return ack(id, "unsupported");
      const state = read(), existing = state.deliveries.find(item => item.deliveryId === id);
      if (existing) return ack(id, existing.homePath === homePath ? "accepted" : "unsupported");
      state.deliveries.push({ deliveryId: id, homePath, acceptedAt: now() });
      state.deliveries = state.deliveries.slice(-MAX_DELIVERIES);
      state.pending = { deliveryId: id, homePath, phase: "pending" };
      if (!write(state)) return ack(id, "not_ready");
      onPending(state.pending);
      return ack(id, "accepted");
    },
    status(deliveryId) {
      return ack(validId(deliveryId) ? deliveryId : "", validId(deliveryId) &&
        read().deliveries.some(item => item.deliveryId === deliveryId) ? "accepted" : "not_ready");
    },
    pending() { return read().pending; },
    markRouted(deliveryId) {
      const state = read();
      if (!state.pending || state.pending.deliveryId !== deliveryId) return false;
      if (state.pending.phase === "routing") return true;
      state.pending = { ...state.pending, phase: "routing" };
      if (!write(state)) return false;
      onPending(state.pending);
      return true;
    },
    finish(deliveryId) {
      const state = read();
      if (!state.pending || state.pending.deliveryId !== deliveryId) return;
      state.pending = null;
      if (write(state)) onPending(null);
    },
  };
}
